import { Pic } from "./Pic";

import { motion } from "framer-motion";
import { AlertTriangle, ArrowRight, ShieldCheck } from "lucide-react";
import { MaskLines, Reveal, SectionLabel } from "./Motion";
import { useGo } from "./Nav";
import { scrollToId } from "@/lib/scroll";
import { GuidePdf } from "./GuidePdf";
import { useT } from "@/i18n";

const ERRORS = [
  {
    img: "/img/emma/errore-umidita.jpg",
    title: "Sottovalutare l'umidità di risalita e il contatto con il terreno",
    text: "Il legno è un materiale vivo e straordinario, ma teme l'umidità stagnante. L'errore più grave avviene alla base della struttura, dove le pareti incontrano le fondamenta in cemento armato.",
    risk: "Il cemento è poroso e assorbe l'acqua dal terreno per capillarità. Se i pannelli in legno poggiano direttamente sulla fondazione senza un cordolo di rialzo o membrane impermeabili adeguate, il legno marcisce alla base.",
    fix: "Isolare rigorosamente l'attacco a terra sollevando le pareti tramite cordoli in cemento o acciaio idonei e applicando guaine impermeabilizzanti continue, sia interne che esterne.",
  },
  {
    img: "/img/emma/errore-impianti.jpg",
    title: "Dimenticare la progettazione integrata degli impianti",
    text: "Nelle costruzioni tradizionali in mattoni le tracce per tubi e cavi elettrici si possono scavare in cantiere in qualsiasi momento. Nelle case in legno prefabbricate questo approccio è vietato.",
    risk: "I pannelli (X-LAM o a telaio) vengono tagliati al millimetro in fabbrica con macchinari a controllo numerico. Bucare una parete a posteriori per aggiungere una presa o un tubo significa danneggiare la statica dell'edificio e rompere la barriera al vapore, creando pericolose condense interne.",
    fix: "Scegliere la progettazione integrata: ogni foro, interruttore, predisposizione idraulica o passaggio della climatizzazione va definito al computer prima che la casa venga prodotta.",
  },
  {
    img: "/img/emma/errore-ponti-termici.jpg",
    title: "Trascurare i ponti termici e la tenuta all'aria",
    text: "Una casa in legno moderna punta a essere un'eccellenza termica, spesso vicina agli standard Passive House. Ma un isolamento non continuo cancella ogni beneficio.",
    risk: "Se i nodi tra pareti, serramenti, tetto e balconi non sono progettati al millimetro si creano ponti termici. Il calore scappa, la parete si raffredda localmente e lo sbalzo termico genera condensa superficiale, che si trasforma rapidamente in muffa.",
    fix: "Usare nastri sigillanti specifici per la tenuta all'aria, teli freno vapore posati a regola d'arte e progettare una Ventilazione Meccanica Controllata (VMC) per un ricambio d'aria continuo che elimina l'umidità interna.",
  },
  {
    img: "/img/emma/errore-logistica.jpg",
    title: "Gestire con superficialità logistica e tempi di cantiere",
    text: "La velocità di montaggio è un punto di forza unico: una casa in legno può essere eretta e impermeabilizzata in pochi giorni. Ma questa rapidità richiede una macchina organizzativa impeccabile.",
    risk: "I materiali prefabbricati arrivati in cantiere non possono restare esposti a intemperie o fango per settimane a causa di ritardi nello scavo o nella burocrazia. E le modifiche in corso d'opera non sono ammesse: non si può spostare una finestra mentre i carpentieri assemblano i moduli.",
    fix: "Coordinare perfettamente i tempi di getto delle fondazioni con la produzione in fabbrica e affidarsi a un'unica azienda strutturata o a un direttore dei lavori esperto in strutture di legno.",
  },
];

const Box = ({ fix, label, children, testid }) => (
  <div className={`border-l-2 pl-5 ${fix ? "border-[#C26D38]" : "border-[#B3412E]"}`} data-testid={testid}>
    <p className={`flex items-center gap-2 font-mono text-xs uppercase tracking-[0.2em] ${fix ? "text-[#9A5226]" : "text-[#B3412E]"}`}>
      {fix ? <ShieldCheck className="h-4 w-4" /> : <AlertTriangle className="h-4 w-4" />} {label}
    </p>
    <p className="mt-2 text-base leading-relaxed text-[#3D3833]">{children}</p>
  </div>
);

const ErrorRow = ({ e, i, t }) => (
  <article className="grid gap-8 border-t border-[#121110]/15 pt-12 lg:grid-cols-12 lg:gap-14" data-testid={`guide-error-${i}`}>
    <motion.div
      initial={{ opacity: 0, y: 40 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.15 }}
      transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
      className={`lg:col-span-5 ${i % 2 ? "lg:order-2" : ""}`}
    >
      <div className="overflow-hidden bg-[#1E1C1A]">
        <Pic src={e.img} alt={t(`guide.e${i}.t`, e.title)} loading="lazy" className="aspect-[5/4] w-full object-cover transition-transform duration-[1.4s] hover:scale-105" />
      </div>
    </motion.div>
    <Reveal className="lg:col-span-7" delay={0.1}>
      <span className="font-display text-6xl font-bold text-[#C26D38] sm:text-7xl">{String(i + 1).padStart(2, "0")}</span>
      <h3 className="mt-4 font-display text-2xl font-bold leading-tight tracking-tight sm:text-3xl">{t(`guide.e${i}.t`, e.title)}</h3>
      <p className="mt-4 text-base leading-relaxed text-[#6E685E] sm:text-lg">{t(`guide.e${i}.d`, e.text)}</p>
      <div className="mt-8 grid gap-6 xl:grid-cols-2">
        <Box label={t("guide.risk", "Il rischio")} testid={`guide-risk-${i}`}>{t(`guide.e${i}.r`, e.risk)}</Box>
        <Box fix label={t("guide.fix", "La soluzione")} testid={`guide-fix-${i}`}>{t(`guide.e${i}.s`, e.fix)}</Box>
      </div>
    </Reveal>
  </article>
);

export const TechGuide = () => {
  const t = useT();
  return (
    <section id="guida-tecnica" className="bg-[#EFECE6] px-5 pb-20 pt-36 text-[#121110] sm:px-8 lg:pb-28 lg:pt-44" data-testid="guide-section">
      <div className="mx-auto max-w-[1500px]">
        <div className="grid gap-12 lg:grid-cols-12 lg:items-end">
          <div className="lg:col-span-7">
            <SectionLabel index="!">{t("guide.label", "Guida tecnica")}</SectionLabel>
            <MaskLines
              as="h1"
              onLoad
              className="mt-10 font-display text-4xl font-bold leading-[1.02] tracking-tight sm:text-5xl lg:text-[4.2rem]"
              lines={[t("guide.h1", "4 errori fatali"), <span key="b">{t("guide.h2a", "da evitare nelle")} <em className="font-serif font-normal text-[#C26D38]">{t("guide.h2em", "case in legno.")}</em></span>]}
            />
            <Reveal delay={0.2} className="mt-8 max-w-2xl space-y-4 text-base leading-relaxed text-[#6E685E] sm:text-lg">
              <p>{t("guide.intro1", "Una casa prefabbricata in legno garantisce prestazioni energetiche straordinarie e tempi di cantiere record. Ma il legno richiede una precisione millimetrica: un solo dettaglio trascurato in fase di progettazione può compromettere la durabilità dell'intera struttura.")}</p>
              <p>{t("guide.intro2", "Ecco i 4 errori più frequenti nei cantieri e le soluzioni tecniche per proteggere il tuo investimento.")}</p>
              <p className="font-mono text-xs uppercase tracking-[0.2em] text-[#6E685E]" data-testid="guide-byline">{t("guide.byline", "A cura dell'ufficio tecnico Emma Industrie")}</p>
            </Reveal>
            <Reveal delay={0.3} className="mt-10">
              <p className="font-mono text-xs uppercase tracking-[0.2em] text-[#C26D38]">{t("guide.index", "In questa pagina")}</p>
              <ol className="mt-3 space-y-2">
                {[["#errori", t("guide.idx0", "01 · 4 errori fatali da evitare")], ["#xlam-o-telaio", t("guide.idx1", "02 · Come scegliere tra X-LAM e telaio")]].map(([id, label]) => (
                  <li key={id}>
                    <button onClick={() => scrollToId(id, -110)} className="tap group inline-flex items-center gap-2 border-b border-[#121110]/25 pb-1 text-left font-semibold transition-colors hover:border-[#C26D38] hover:text-[#C26D38]" data-testid={`guide-index-${id.slice(1)}`}>
                      {label} <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                    </button>
                  </li>
                ))}
              </ol>
            </Reveal>
          </div>
          <Reveal className="lg:col-span-5" delay={0.15}>
            <Pic src="/img/emma/errori-intro.jpg" alt={t("guide.introAlt", "Parete a telaio degradata da errori costruttivi")} className="aspect-[5/4] w-full object-cover grayscale-[30%]" />
          </Reveal>
        </div>
        <div id="errori" className="mt-20 space-y-16">
          {ERRORS.map((e, i) => <ErrorRow key={e.img} e={e} i={i} t={t} />)}
        </div>
      </div>
    </section>
  );
};

export const GuideOutro = () => {
  const t = useT();
  const go = useGo();
  return (
    <section className="bg-[#EFECE6] px-5 py-24 text-[#121110] sm:px-8 lg:py-32" data-testid="guide-outro">
      <div className="mx-auto max-w-[1500px]">
        <GuidePdf />
        <Reveal className="mt-6 flex flex-col items-start gap-6 border-2 border-[#121110] p-8 sm:flex-row sm:items-center sm:justify-between sm:p-12" data-testid="guide-cta-box">
          <p className="max-w-2xl font-display text-2xl font-bold leading-tight sm:text-3xl">
            {t("guide.ctaText", "Ognuno di questi errori lo abbiamo già visto, e risolto.")} <em className="font-serif font-normal text-[#C26D38]">{t("guide.ctaEm", "Per questo non lo ripetiamo.")}</em>
          </p>
          <button onClick={() => go("#contatti")} className="btn-amber shrink-0" data-testid="guide-cta">
            {t("guide.cta", "Progetta con noi")} <ArrowRight className="h-4 w-4" />
          </button>
        </Reveal>
      </div>
    </section>
  );
};
